import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import Icon from '../Icon';
import {
  CompletionMsg,
  CourseActionsWrapper,
  ProgressBarWrapper,
  ShowStatusContainer,
} from './style';
import ProgressBar from '../ProgressBar';
import CTA from '../CTA';
import Text from '../Text';
import { withMediaQueries } from '../../../app/hoc/withMediaQueries';
import { apiHost, baseUrl } from '../../../app/api/config';
import { getToken } from '../../../app/api/token';
import { ToastError } from '../../components/Toast';

const CourseActions = ({
  mediaIsPhone,
  item,
  showStatus,
  onClick,
  onClickLeave,
  onClickSurvey,
  completedSurveys,
}) => {
  const downloadRef = useRef(null);
  const [fileError, setFileError] = useState(false);

  useEffect(() => {
    const token = getToken()?.token;
    if (
      token &&
      item?.id &&
      item?.completion_certificate &&
      item?.completion?.status === 'completed' &&
      downloadRef
    ) {
      fetch(`${apiHost}${baseUrl}pzzl_certificate/course/${item.id}`, {
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          Authorization: `Bearer ${token}`,
        },
      })
        .then((res) => {
          if (!res?.ok) throw new Error();
          return res.blob();
        })
        .then((blob) => {
          const href = window.URL.createObjectURL(blob);
          const a = downloadRef.current;
          a.download = `certificato ${item?.title || item?.id}.pdf`;
          a.href = href;
        })
        .catch((err) => {
          if (
            process.env.NODE_ENV === 'development' ||
            process.env.NODE_ENV === 'test'
          ) {
            // eslint-disable-next-line no-console
            console.error('Error during certificate download:', err);
          } else {
            // eslint-disable-next-line no-console
            console.error('Error during certificate download');
          }
          setFileError(true);
        });
    }
  }, [item, downloadRef]);

  useEffect(() => {
    if (fileError) {
      toast.error(
        <ToastError
          title="Qualcosa è andato storto!"
          body="Si prega di riprovare più tardi"
        />
      );
    }
  }, [fileError]);

  const getProgressStatus = () => {
    if (item?.completion?.status === 'incomplete') {
      return (
        <ShowStatusContainer>
          <Text type="b4" className="last-activity">
            Stai seguendo:{' '}
            <b>
              Attività {item?.current_activity?.number}{' '}
              {item?.current_activity?.name}
            </b>{' '}
            <Icon type="marker-pin-06" />
          </Text>
          <ProgressBarWrapper>
            <ProgressBar
              percentage={Math.round((item?.completion?.rate || 0) * 100)}
              hasPercentage
              bgColor="#D4D4D4"
              color="#22408B"
              height={10}
            />
          </ProgressBarWrapper>
        </ShowStatusContainer>
      );
    }
    return <div />;
  };

  if (item?.completion?.status === 'empty') {
    return (
      <CTA
        onClick={onClick}
        btnText="Dettaglio"
        type={mediaIsPhone ? 'primary' : 'tertiary'}
        icon="arrow-narrow-right"
        iconSize={18}
        bottomRight
        fullsize={mediaIsPhone}
      />
    );
  }

  if (item?.completion?.status === 'completed') {
    return (
      <CourseActionsWrapper className="completed">
        {showStatus && (
          <div className="complete-msg-wrapper">
            <CompletionMsg>
              Corso completato il{' '}
              {dayjs((item?.completion?.completion_date || 0) * 1000).format(
                'DD/MM/YYYY'
              )}
              <Icon type="check-circle" size={16} />
            </CompletionMsg>
            {item?.completion_certificate && (
              <Text type="b3" className="certificate-message">
                È disponibile l’attestato di frequenza.
              </Text>
            )}
          </div>
        )}
        {item?.completion_certificate && (
          <a
            href="#"
            ref={downloadRef}
            className="download"
            aria-label="Download"
          >
            <CTA
              btnText="download"
              type={mediaIsPhone ? 'secondary' : 'tertiary'}
              icon="download-01"
              iconSize={18}
              fullsize={mediaIsPhone}
            />
          </a>
        )}
        {item?.satisfaction_survey?.completion_status === 'incomplete' &&
          !completedSurveys?.includes(item?.id) && (
            <CTA
              onClick={onClickSurvey}
              btnText="Questionario"
              type={mediaIsPhone ? 'secondary' : 'tertiary'}
              icon="edit-02"
              iconSize={18}
              fullsize={mediaIsPhone}
            />
          )}
        <CTA
          onClick={onClick}
          btnText="Dettaglio"
          type={mediaIsPhone ? 'primary' : 'tertiary'}
          icon="arrow-narrow-right"
          iconSize={18}
          bottomRight
          fullsize={mediaIsPhone}
        />
      </CourseActionsWrapper>
    );
  }

  return (
    <CourseActionsWrapper className="in-progress">
      {showStatus && getProgressStatus()}
      {item?.typology === 'course' && (
        <CTA
          onClick={onClickLeave}
          btnText="Abbandona"
          type={mediaIsPhone ? 'secondary' : 'tertiary'}
          icon="x-close"
          iconSize={18}
          fullsize={mediaIsPhone}
        />
      )}
      <CTA
        onClick={onClick}
        btnText="Riprendi"
        type={mediaIsPhone ? 'primary' : 'tertiary'}
        icon="arrow-narrow-right"
        iconSize={18}
        bottomRight
        fullsize={mediaIsPhone}
      />
    </CourseActionsWrapper>
  );
};

CourseActions.propTypes = {
  mediaIsPhone: PropTypes.bool,
  item: PropTypes.object.isRequired,
  showStatus: PropTypes.bool,
  onClick: PropTypes.func,
  onClickLeave: PropTypes.func,
  onClickSurvey: PropTypes.func,
  completedSurveys: PropTypes.array,
};

export default connect((state) => ({
  completedSurveys: state.app.completedSurveys,
}))(withMediaQueries(CourseActions));
